import { ChangeEvent, cloneElement, ReactElement, useState } from "react";
import { SelectFieldType } from "shared/types";
import { FormFieldEnums } from "shared/types/enums";
import FloatingInputWrapper from "shared/components/layouts/FloatingInputWrapper";
import BottomSheet from "shared/components/common/BottomSheet";
import TextInput from "./TextInput";
import CountryOptions from "shared/components/common/CountryOptions";
import IconButton from "../buttons/IconButton";
import "styles/components/forms/inputs/searchInput.css";

type OptionsProps = {
  onOptionSelect?: (option: { name: string; value: string }) => void;
};

type SelectInputProps = SelectFieldType & {
  options?: ReactElement<OptionsProps>;
  onSelected?: (selectedValue: { name: string; value: string }) => void;
  withSearch?: boolean;
  title?: string;
  detent?: "content-height" | "full-height";
};

const SelectInput = ({
  type,
  required,
  placeholder,
  id,
  className,
  value,
  options,
  onSelected,
  withSearch = false,
  title = "",
  detent = "full-height",
}: SelectInputProps) => {
  const [searchValue, setSearchValue] = useState("");
  const [selectedOption, setSelectedOption] = useState(value ? value : "");
  const [isButtonSheetVisable, setButtonSheetVisable] = useState(false);

  const onOptionSelect = (option: { name: string; value: string }) => {
    setSelectedOption(option.name);
    setButtonSheetVisable(false);
    setSearchValue("");
    if (onSelected && option) {
      onSelected(option);
    }
  };

  const renderOptions = () => {
    switch (type) {
      case FormFieldEnums.CountriesSelect:
        return (
          <CountryOptions
            query={searchValue}
            selectedCallback={onOptionSelect}
          />
        );
      case FormFieldEnums.Select:
        if (options) {
          return cloneElement(options, { onOptionSelect });
        }
        return <div>No Options found</div>;
      default:
        return <div>No select found</div>;
    }
  };

  const renderSelect = () => {
    return (
      <FloatingInputWrapper
        id={id}
        placeholder={placeholder}
        shrink={selectedOption ? true : false}
      >
        <input
          value={selectedOption}
          className={className}
          readOnly
          required={required}
          name={id}
          id={id}
        />
        <IconButton
          iconName="ArrowIcon"
          wrapperClass="select-icon"
          svgProp={{ width: 16, height: 16 }}
        />
      </FloatingInputWrapper>
    );
  };

  return (
    <>
      <div
        className="select-container"
        onClick={() =>
          setButtonSheetVisable((prev) => {
            return !prev;
          })
        }
      >
        {renderSelect()}
      </div>
      <BottomSheet
        isOpen={isButtonSheetVisable}
        onClose={() => setButtonSheetVisable(false)}
        title={title}
        detent={detent}
        defaultCloseButton={false}
      >
        {withSearch && (
          <div className="select-container">
            <TextInput
              id={"search"}
              value={searchValue}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setSearchValue(e.target.value)
              }
              placeholder={"Search"}
              required={false}
              type={FormFieldEnums.Text}
              iconName={"SearchIcon"}
            />
          </div>
        )}

        {/* Render the options based on the type */}
        <div className="options-container">{renderOptions()}</div>
      </BottomSheet>
    </>
  );
};

export default SelectInput;
